import { useState } from 'react';

export default function Funders() {
  const [companyAge, setCompanyAge] = useState<'new' | 'established'>('new');
  const [revenue, setRevenue] = useState(0);
  const [hasReport, setHasReport] = useState(false);

  // 計算預估額度
  const calculateLoanRange = () => {
    let min = 0;
    let max = 0;

    if (companyAge === 'new') {
      // 未滿 1 年（創業）
      min = 50;
      max = 200 + (revenue * 0.5);
      if (max > 1200) max = 1200;
    } else {
      // 1 年以上（營運中）
      if (hasReport) {
        // 有 401/403 報表
        min = revenue * 0.5;
        max = revenue * 1.2;
      } else {
        // 無 401/403 報表
        min = 30;
        max = 100 + (revenue * 0.2);
        if (max > 300) max = 300;
      }
    }

    return { 
      min: Math.round(min), 
      max: Math.round(max) 
    };
  };

  const loanRange = calculateLoanRange();

  // 格式化金額顯示
  const formatAmount = (amount: number) => {
    if (amount >= 10000) {
      return `${(amount / 10000).toFixed(1)} 億`;
    }
    return `${amount} 萬`;
  };

  // 建議方案
  const getRecommendedPrograms = () => {
    const programs = [];
    
    if (companyAge === 'new') {
      programs.push('青年創業專案');
    } else {
      if (hasReport) {
        programs.push('中小企業信保');
      } else {
        programs.push('小頭家貸款');
      }
    }
    
    programs.push('低碳設備補助');
    
    return programs;
  };

  const recommendedPrograms = getRecommendedPrograms();

  return (
    <section id="funders" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
          {/* 左側：說明與信任建立 */}
          <div className="lg:col-span-1">
            <div className="inline-block px-4 py-2 bg-[#FF8C42]/10 rounded-full mb-6">
              <span className="text-[#FF8C42] font-semibold text-sm">
                <i className="ri-calculator-line mr-2"></i>
                免費額度試算
              </span>
            </div>

            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              您的企業能貸多少？
            </h2>
            <p className="text-2xl font-bold text-[#FF8C42] mb-8">
              1 分鐘快速評估
            </p>

            <p className="text-gray-700 mb-6 leading-relaxed">
              透過簡單的條件輸入，<strong>初步估算</strong>您可申請的資金額度範圍。此試算結果僅供參考，實際額度仍需視公司詳細財報與銀行審核而定。
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg mb-8">
              <div className="flex items-start">
                <i className="ri-information-line text-blue-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                <div>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    <strong>小提醒：</strong>擁有完整的 401/403 報表與良好信用紀錄，是爭取高額度與低利率的關鍵。
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 右側：互動式試算器 */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            {/* 試算器標題區 */}
            <div className="bg-gradient-to-r from-[#1a5f7a] to-[#0d3d52] px-8 py-6">
              <h3 className="text-2xl font-bold text-white mb-2">資金額度試算器</h3>
              <p className="text-white/90 text-sm">
                請輸入您的企業現況，系統將自動演算建議區間
              </p>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* 輸入區（左半部） */}
                <div className="space-y-8">
                  {/* 輸入條件一：公司成立時間 */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-4">
                      1. 公司成立時間
                    </label>
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => {
                          setCompanyAge('new');
                          setHasReport(false);
                        }}
                        className={`w-full px-6 py-4 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap text-left ${
                          companyAge === 'new'
                            ? 'bg-[#FF8C42] text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <div className="font-bold text-base mb-1">未滿 1 年</div>
                        <div className="text-xs opacity-90">創業籌備期</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setCompanyAge('established')}
                        className={`w-full px-6 py-4 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap text-left ${
                          companyAge === 'established'
                            ? 'bg-[#FF8C42] text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <div className="font-bold text-base mb-1">1 年以上</div>
                        <div className="text-xs opacity-90">營運擴張期</div>
                      </button>
                    </div>
                  </div>

                  {/* 輸入條件二：年營收預估 */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-4">
                      2. 年營收預估（萬元）
                      <span className="ml-4 text-xl font-bold text-[#FF8C42]">{revenue} 萬</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="5000"
                      step="10"
                      value={revenue}
                      onChange={(e) => setRevenue(Number(e.target.value))}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#FF8C42]"
                      style={{
                        background: `linear-gradient(to right, #FF8C42 0%, #FF8C42 ${(revenue / 5000) * 100}%, #e5e7eb ${(revenue / 5000) * 100}%, #e5e7eb 100%)`
                      }}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>0</span>
                      <span>1000萬</span>
                      <span>3000萬</span>
                      <span>5000萬+</span>
                    </div>
                  </div>

                  {/* 輸入條件三：是否有 401/403 報表（僅 1 年以上顯示） */}
                  {companyAge === 'established' && (
                    <div>
                      <label className="flex items-center justify-between cursor-pointer">
                        <div>
                          <div className="text-sm font-bold text-gray-900">
                            3. 是否有 401/403 報表？
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            正式稅務申報紀錄
                          </div>
                        </div>
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={hasReport}
                            onChange={(e) => setHasReport(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-14 h-7 bg-gray-300 rounded-full peer peer-checked:bg-[#FF8C42] transition-colors"></div>
                          <div className="absolute left-1 top-1 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-7"></div>
                        </div>
                      </label>
                    </div>
                  )}
                </div>

                {/* 結果區（右半部） */}
                <div className="space-y-6">
                  {/* 試算結果顯示 */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 border-2 border-gray-200">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-4">預估可貸額度範圍</p>
                      <div className="flex items-center justify-center gap-3">
                        <span className="text-3xl font-bold text-[#FF8C42]">
                          {formatAmount(loanRange.min)}
                        </span>
                        <span className="text-xl text-gray-400">～</span>
                        <span className="text-3xl font-bold text-[#FF8C42]">
                          {formatAmount(loanRange.max)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 建議申請方案 */}
                  <div>
                    <p className="text-sm font-bold text-gray-900 mb-3">建議申請方案</p>
                    <div className="flex flex-wrap gap-2">
                      {recommendedPrograms.map((program, index) => (
                        <span
                          key={index}
                          className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold"
                        >
                          <i className="ri-checkbox-circle-line mr-1"></i>
                          {program}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* 行動按鈕 */}
                  <button
                    type="button"
                    onClick={() => {
                      // 導向聯絡頁，並帶入條件
                      const params = new URLSearchParams({
                        companyAge: companyAge === 'new' ? '未滿1年' : '1年以上',
                        revenue: revenue.toString(),
                        hasReport: hasReport ? '是' : '否'
                      });
                      window.REACT_APP_NAVIGATE(`/contact?${params.toString()}`);
                    }}
                    className="w-full bg-gradient-to-r from-[#1a5f7a] to-[#0d3d52] text-white py-4 rounded-xl font-bold text-base hover:from-[#15526a] hover:to-[#0a3244] transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    索取詳細規劃書
                    <i className="ri-arrow-right-line text-xl"></i>
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    * 試算結果僅供參考，實際額度以銀行最終核貸為準。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
